// frontend/src/features/livestock/CompareShotPicker.tsx
// Choosing the shot for one side of the compare page (design 7.1): preset
// chips (Before: 1 month ... First; After: Latest), a filmstrip of every shot,
// and By date as a secondary option (a modal calendar on a phone). Any
// subject's photos: the words default to tank shots.

import { useEffect, useRef, useState } from 'react';
import { Button, Card, Group, Modal, Popover, Stack, Text, UnstyledButton } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { IconCalendar, IconPhoto } from '@tabler/icons-react';
import type { PhotoIndexItem } from '@/types/photos';
import { BRAND_TEXT } from './brandText';
import { FramedBox, ratioOf } from './CompareImage';
import type { CompareSide } from './compareShots';
import { parseServerDate } from './monthGroups';
import { TANK_WORDS, capitalise, type SubjectWords } from './photoSubject';
import { framedImageStyle } from './framing';
import { formatPhotoDay } from './photoInboxFormat';
import classes from './Compare.module.css';

export interface PickerChip {
  key: string;
  label: string;
  /** The shot the chip picks; null disables it. */
  targetId: number | null;
  active: boolean;
}

export interface CompareShotPickerProps {
  side: CompareSide;
  /** Every shot, oldest first. */
  items: PhotoIndexItem[];
  selectedId: number;
  /** The other side's shot: marked, and refused if picked. */
  otherId: number;
  chips: PickerChip[];
  /** "Nearest shot: 3 Mar 2026" after a preset or a date; null otherwise. */
  nearestLine: string | null;
  /** "Same shot, pick another" and the like, until the next pick. */
  notice: string | null;
  isMobile: boolean;
  onChip: (chip: PickerChip) => void;
  onPick: (id: number) => void;
  /** A day picked By date ("YYYY-MM-DD"). */
  onDate: (day: string) => void;
  onImageError?: () => void;
  /** The subject's words ("shot" or "photo"); tank shots by default. */
  words?: SubjectWords;
}

/** 44 px calendar controls on a phone, as the inbox's date picker (photo inbox 4.4). */
const PHONE_CALENDAR_STYLES = {
  calendarHeaderControl: { width: 44, height: 44 },
  calendarHeaderLevel: { height: 44 },
  day: { width: 44, height: 44 },
};

/** A filmstrip thumb, framed as the shot is lined up so it looks as it will compare. */
function StripThumb({
  item,
  onImageError,
}: {
  item: PhotoIndexItem;
  onImageError?: () => void;
}): JSX.Element {
  const [photoRatio, setPhotoRatio] = useState<number | null>(null);
  const framing = item.framing ?? null;
  const img = (
    <img
      className={framing ? undefined : classes.stripThumb}
      src={item.thumb_url ?? undefined}
      alt=""
      loading="lazy"
      decoding="async"
      draggable={false}
      style={framing ? framedImageStyle(framing) : undefined}
      onLoad={(event) => setPhotoRatio(ratioOf(event))}
      onError={onImageError}
    />
  );
  if (!framing) return img;
  return (
    <span className={`${classes.stripThumb} ${classes.stripFramed}`}>
      <FramedBox framing={framing} photoRatio={photoRatio} fit="cover">
        {img}
      </FramedBox>
    </span>
  );
}

function Filmstrip({
  side,
  items,
  selectedId,
  otherId,
  onPick,
  onImageError,
  words,
}: Pick<
  CompareShotPickerProps,
  'side' | 'items' | 'selectedId' | 'otherId' | 'onPick' | 'onImageError'
> & { words: SubjectWords }): JSX.Element {
  const stripRef = useRef<HTMLDivElement>(null);
  const otherLabel = side === 'before' ? 'After' : 'Before';

  // Bring the chosen shot into view within the strip (not the page).
  useEffect(() => {
    const strip = stripRef.current;
    const tile = strip?.querySelector<HTMLElement>('[data-selected]');
    if (!strip || !tile) return;
    strip.scrollLeft = tile.offsetLeft - strip.clientWidth / 2 + tile.offsetWidth / 2;
  }, [selectedId]);

  return (
    <div
      className={classes.strip}
      ref={stripRef}
      role="group"
      aria-label={`${capitalise(words.nounPlural)} for ${side}`}
    >
      {items.map((item) => {
        const selected = item.id === selectedId;
        const other = item.id === otherId;
        const day = formatPhotoDay(item.entry_date);
        return (
          <UnstyledButton
            key={item.id}
            className={classes.stripTile}
            data-selected={selected || undefined}
            data-other={other || undefined}
            aria-pressed={selected}
            aria-label={other ? `${day}, the ${otherLabel} ${words.noun}` : day}
            onClick={() => onPick(item.id)}
          >
            {item.thumb_url ? (
              <StripThumb item={item} onImageError={onImageError} />
            ) : (
              <span className={classes.stripThumb}>
                <IconPhoto size={16} color="var(--mantine-color-gray-7)" />
              </span>
            )}
            <span className={classes.stripDate}>
              {other ? otherLabel : parseServerDate(item.entry_date).format('D MMM YY')}
            </span>
          </UnstyledButton>
        );
      })}
    </div>
  );
}

export function CompareShotPicker({
  side,
  items,
  selectedId,
  otherId,
  chips,
  nearestLine,
  notice,
  isMobile,
  onChip,
  onPick,
  onDate,
  onImageError,
  words = TANK_WORDS,
}: CompareShotPickerProps): JSX.Element {
  const [dateOpen, setDateOpen] = useState(false);
  const selected = items.find((item) => item.id === selectedId);
  const title = side === 'before' ? 'Before' : 'After';
  const controlHeight = isMobile ? 44 : 36;
  const first = items[0];
  const latest = items[items.length - 1];

  const pickDate = (value: string | null): void => {
    setDateOpen(false);
    if (value) onDate(value);
  };

  const calendar = (
    <DatePicker
      value={selected ? parseServerDate(selected.entry_date).format('YYYY-MM-DD') : null}
      onChange={pickDate}
      minDate={first ? parseServerDate(first.entry_date).format('YYYY-MM-DD') : undefined}
      maxDate={latest ? parseServerDate(latest.entry_date).format('YYYY-MM-DD') : undefined}
      defaultDate={selected ? parseServerDate(selected.entry_date).format('YYYY-MM-DD') : undefined}
      styles={isMobile ? PHONE_CALENDAR_STYLES : undefined}
      aria-label={`${title} date`}
    />
  );

  const byDate = (
    <Button
      variant="subtle"
      size="compact-sm"
      h={controlHeight}
      leftSection={<IconCalendar size={16} />}
      onClick={() => setDateOpen((open) => !open)}
      style={BRAND_TEXT}
    >
      By date
    </Button>
  );

  return (
    <Card withBorder radius="xl" p={{ base: 'sm', md: 'md' }}>
      <Stack gap="xs">
        <Group justify="space-between" gap="xs" wrap="nowrap">
          <Text fw={600} className="font-heading" fz={{ base: 16, sm: 18 }}>
            {title}
          </Text>
          <Text size="sm" fw={500} className={classes.tabular}>
            {selected ? formatPhotoDay(selected.entry_date) : ''}
          </Text>
        </Group>

        <Group gap={6} wrap="wrap" role="group" aria-label={`${title} presets`}>
          {chips.map((chip) => (
            <Button
              key={chip.key}
              size="compact-sm"
              h={controlHeight}
              px="sm"
              radius="xl"
              variant={chip.active ? 'light' : 'default'}
              aria-pressed={chip.active}
              disabled={chip.targetId === null}
              style={chip.active ? BRAND_TEXT : undefined}
              onClick={() => onChip(chip)}
            >
              {chip.label}
            </Button>
          ))}
        </Group>

        <Filmstrip
          side={side}
          items={items}
          selectedId={selectedId}
          otherId={otherId}
          onPick={onPick}
          onImageError={onImageError}
          words={words}
        />

        <Group gap="xs" wrap="wrap" align="center">
          {isMobile ? (
            byDate
          ) : (
            <Popover opened={dateOpen} onChange={setDateOpen} position="bottom-start" withinPortal>
              <Popover.Target>{byDate}</Popover.Target>
              <Popover.Dropdown>{calendar}</Popover.Dropdown>
            </Popover>
          )}
          {nearestLine && (
            <Text size="sm" c="gray.7" className={classes.tabular}>
              {nearestLine}
            </Text>
          )}
        </Group>
      </Stack>
      {/* Always mounted (empty, no height, when there is nothing to say) so a new notice is announced. */}
      <Text size="sm" c="coral.7" role="status" mt={notice ? 'xs' : 0}>
        {notice ?? ''}
      </Text>

      {isMobile && (
        <Modal
          opened={dateOpen}
          onClose={() => setDateOpen(false)}
          title={`${title}: pick a date`}
          size="auto"
          centered
        >
          {calendar}
          <Text size="xs" c="gray.7" mt="xs">
            The nearest {words.noun} to the day is chosen.
          </Text>
        </Modal>
      )}
    </Card>
  );
}
